import React, { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { BookOpen, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw.length < 6) return setError("Password must be at least 6 characters");
    if (pw !== confirm) return setError("Passwords do not match");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      toast.success("Password updated");
      setTimeout(() => navigate("/login", { replace: true }), 1800);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-8 bg-background">
      <div className="w-full max-w-sm animate-fade-up" data-testid="reset-password-page">
        <div className="flex items-center gap-2 font-display text-2xl font-semibold mb-8">
          <BookOpen className="text-primary" /> Khata
        </div>
        {!token ? (
          <div className="rounded-xl border border-border bg-card p-6 text-center text-muted-foreground" data-testid="reset-no-token">
            This reset link is invalid. <Link to="/login" className="text-primary hover:underline">Back to sign in</Link>
          </div>
        ) : done ? (
          <div className="rounded-xl border border-border bg-card p-6 text-center" data-testid="reset-done">
            <CheckCircle2 className="mx-auto mb-3 text-[hsl(var(--positive))]" size={36} />
            <h1 className="font-display text-2xl font-semibold">Password updated</h1>
            <p className="text-muted-foreground text-sm mt-2">Redirecting you to sign in…</p>
          </div>
        ) : (
          <form onSubmit={submit}>
            <h1 className="font-display text-3xl font-semibold">Set a new password</h1>
            <p className="text-muted-foreground text-sm mt-1 mb-8">Choose a strong password you'll remember.</p>
            <label className="block text-sm font-medium mb-1.5">New password</label>
            <input
              data-testid="reset-password"
              type="password"
              required
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              className="w-full h-11 px-3 rounded-md border border-input bg-card text-sm mb-4 focus:outline-none focus:ring-2 focus:ring-ring/40"
              placeholder="••••••••"
            />
            <label className="block text-sm font-medium mb-1.5">Confirm password</label>
            <input
              data-testid="reset-confirm"
              type="password"
              required
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="w-full h-11 px-3 rounded-md border border-input bg-card text-sm mb-2 focus:outline-none focus:ring-2 focus:ring-ring/40"
              placeholder="••••••••"
            />
            {error && <p data-testid="reset-error" className="text-destructive text-sm mb-2">{error}</p>}
            <button
              data-testid="reset-submit"
              disabled={loading}
              className="mt-4 w-full h-11 rounded-md bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors disabled:opacity-60"
            >
              {loading ? "Updating…" : "Update password"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
